import React, { useEffect, useMemo, useState } from 'react';
import axiosInstance from '../../api/axiosInstance';

type OEmbedData = {
  title?: string;
  thumbnail?: string;
  author?: string;
  url?: string;
  description?: string;
  movieName?: string;
  musicDirector?: string;
  singer?: string;
  lyricist?: string;
  year?: string;
  language?: string;
};

const oembedCache = new Map<string, OEmbedData>();
const oembedInflight = new Map<string, Promise<OEmbedData>>();

async function fetchOembed(url: string, enhanced: boolean = false): Promise<OEmbedData> {
  const cacheKey = `${url}_${enhanced}`;
  const cached = oembedCache.get(cacheKey);
  if (cached) return cached;

  const inflight = oembedInflight.get(cacheKey);
  if (inflight) return inflight;

  const p = axiosInstance
    .get('/api/oembed', { params: { url, enhanced: enhanced ? 'true' : 'false' } })
    .then(res => (res?.data ?? {}) as OEmbedData)
    .catch(() => ({ url } as OEmbedData))
    .finally(() => {
      oembedInflight.delete(cacheKey);
    });

  oembedInflight.set(cacheKey, p);
  const data = await p;
  oembedCache.set(cacheKey, data);
  return data;
}

interface YouTubePreviewProps {
  url: string;
  enhanced?: boolean;
}

export const YouTubePreview: React.FC<YouTubePreviewProps> = ({ url, enhanced = false }) => {
  const cacheKey = `${url}_${enhanced}`;
  const [data, setData] = useState<OEmbedData>(() => oembedCache.get(cacheKey) || { url });
  const [loading, setLoading] = useState<boolean>(() => !oembedCache.has(cacheKey));

  useEffect(() => {
    let active = true;
    setLoading(!oembedCache.has(cacheKey));

    fetchOembed(url, enhanced).then(d => {
      if (!active) return;
      setData(d);
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [url, enhanced, cacheKey]);

  const title = data.title || 'YouTube';
  const thumbnail = data.thumbnail;
  const author = data.author;
  const href = data.url || url;

  const domainLabel = useMemo(() => {
    try {
      const u = new URL(href);
      return u.hostname.replace(/^www\./i, '');
    } catch {
      return 'youtube.com';
    }
  }, [href]);

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="block w-full max-w-xs overflow-hidden rounded-xl border border-gray-700 bg-[#0F1A23] hover:border-gray-500 transition-colors"
      onClick={(e) => e.stopPropagation()}
      title={href}
    >
      <div className="w-full">
        {thumbnail ? (
          <img src={thumbnail} alt={title} className="w-full h-36 object-cover" />
        ) : (
          <div className="w-full h-36 bg-black/30 flex items-center justify-center text-gray-400 text-sm">
            {loading ? 'Loading preview…' : 'Preview unavailable'}
          </div>
        )}
      </div>
      <div className="px-3 py-2">
        <div className="text-sm font-semibold text-gray-100 line-clamp-2">{title}</div>
        
        {/* Enhanced metadata */}
        {(data.movieName || data.musicDirector || data.singer || data.lyricist) && (
          <div className="mt-2 space-y-1">
            {data.movieName && (
              <div className="text-xs text-gray-300">
                <span className="text-gray-500">Movie:</span> {data.movieName}
                {data.year && <span className="text-gray-500 ml-1">({data.year})</span>}
              </div>
            )}
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-400">
              {data.musicDirector && (
                <span><span className="text-gray-500">MD:</span> {data.musicDirector}</span>
              )}
              {data.singer && (
                <span><span className="text-gray-500">Singer:</span> {data.singer}</span>
              )}
              {data.lyricist && (
                <span><span className="text-gray-500">Lyrics:</span> {data.lyricist}</span>
              )}
            </div>
            {data.language && (
              <div className="text-xs text-gray-500">{data.language}</div>
            )}
          </div>
        )}
        
        <div className="mt-1 text-xs text-gray-400 flex items-center gap-2">
          <span className="uppercase tracking-wide">{domainLabel}</span>
          {author ? <span className="opacity-80">• {author}</span> : null}
        </div>
      </div>
    </a>
  );
};


